import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";
import { cached, invalidate, invalidateByPrefix, type CachedOpts } from "../utils/swrCache";
import type { OffenerAustauschDto } from "./vorgangAustauschService";

/**
 * Gestufte Vorgangs-Auswahl (`GET /vorgang/picker`, Cluster VORGANG D1–D3).
 * Rang 1 = Vorgänge des Kontakts, 2 = eigene/Team-Vorgänge, 3 = alle weiteren.
 * Bewusst ein schlankes DTO OHNE `kunde`-Relation — nur `kundeName`.
 */
export type VorgangPickerRang = 1 | 2 | 3;

/**
 * Ein To-do, das ein ZWEITER Vorgang mit anzeigt (PO 05.09.2026). Bewusst
 * schlank: Betreff, Status und Fristen plus der Verweis auf die Heimat. Der
 * VERLAUF des To-dos steht dort, nicht im Gast-Vorgang — der lädt nur seine
 * eigenen vier Quellen.
 */
export interface VerknuepftesTodoDto {
    id: number;
    titel: string;
    status: string;
    /** 'YYYY-MM-DD' oder null. */
    twv: string | null;
    frist: string | null;
    /** Heimat-Vorgang — dort gehört das To-do hin. */
    vorgangId: number;
    vorgangNbr: string | null;
    vorgangTitel: string;
}

export interface VorgangPickerTodo {
    id: number;
    titel: string;
    status: string;
}

export interface VorgangPickerVorgang {
    id: number;
    vorgangNbr: string | null;
    titel: string;
    vorgangsart: string | null;
    status: string;
    twv: string | null;
    frist: string | null;
    createdAt: string;
    kundeId: number | null;
    kundeName: string | null;
    /**
     * Anzahl der aktiven To-dos. Der Mail-Picker zeigt daran, ob sich die Zeile
     * aufklappen lässt — die To-dos selbst holt er erst beim Aufklappen
     * (`pickerTodos`).
     */
    todoAnzahl: number;
}

export interface VorgangPickerItem {
    rang: VorgangPickerRang;
    /**
     * Die angemeldete Person ist selbst beteiligt (nicht nur ihr Team) — teilt
     * Rang 2 in „Deine Vorgänge" und „Team-Vorgänge". Optional, damit ältere
     * Antworten und Test-Attrappen ohne das Feld weiter gelten.
     */
    eigen?: boolean;
    vorgang: VorgangPickerVorgang;
    /** Nur bei aktiver Suche befüllt: treffende Todos dieses Vorgangs (max. 5). */
    todos: VorgangPickerTodo[];
}

/** Ein Zuordnungsziel des Mail-Pickers: entweder der Vorgang oder eines seiner Todos. */
export type VorgangPickerZiel = { vorgangId?: number; todoId?: number };

/** Ein Eintrag im paginierten Vorgänge-Feed (T7): entweder ein Vorgang oder ein standalone Todo. */
export interface VorgangFeedItem {
    kind: "vorgang" | "todo";
    vorgang?: VorgangDto;
    todo?: TodoDto;
}

/**
 * Serverseitige Spaltenfilter des Vorgänge-Feeds (SP3). Alle optional; leere
 * Felder werden nicht gesendet. Id-Listen (boTeamIds/foUserIds/beteiligt/
 * ersteller) gehen komma-separiert an die API; `beteiligt` ist ein ODER über
 * HV/Bearbeiter/zu-Informierende (Entscheidung 40), am Vorgang oder an einem
 * seiner To-dos. `beteiligtRollen` (T-245) schränkt das auf einzelne Rollen
 * ein ("hv" | "bearbeiter" | "info"); leer = alle drei.
 */
export interface FeedFilterParams {
    status?: string;
    titel?: string;
    kontakt?: string;
    boTeamIds?: string[];
    foUserIds?: string[];
    beteiligt?: string[];
    beteiligtRollen?: string[];
    fristVon?: string;
    fristBis?: string;
    twvVon?: string;
    twvBis?: string;
    ersteller?: string[];
    /**
     * Austausch (PO 01.11.2026): „offen" = am Vorgang oder einem To-do ist ein
     * Austausch offen, „ohne" = nirgends, „mitMir" = offen und ich stehe dabei.
     */
    austausch?: FeedAustauschFilter;
    /**
     * … und mit wem (Filter „Art & Betreff", PO 01.11.2026): Kollegen per
     * userId, ein Externer per Namensteil. Allein gesetzt = „offen mit …".
     */
    austauschMit?: string[];
    austauschExtern?: string;
}

export const FEED_AUSTAUSCH_FILTER = ["offen", "mitMir", "ohne"] as const;
export type FeedAustauschFilter = (typeof FEED_AUSTAUSCH_FILTER)[number];

/* ────────── Linked entity ────────── */

export type LinkedEntityType =
    | "KUNDE"
    | "VERTRAG"
    | "RISIKO"
    | "KENNZAHL"
    | "ANALYSE"
    | "SCHADEN";

/** Fachlicher Empfänger der nächsten Schaden-Wiedervorlage. */
export type TwvZielgruppe =
    | "VN_AM_ZUG"
    | "VR_AM_ZUG"
    | "RS_INTERN"
    | "CO_MEETING";

/* ────────── Types (mirror backend entities) ────────── */

/** Worauf ein Notizblock zeigen kann (Spiegel von NoteRefArt im Backend). */
export type NoteRefArt = "mail" | "anruf" | "dokument" | "risiko" | "vertrag" | "person" | "todo";

/** Ein Verweis aus einem Block heraus; `label` ist eine Anzeigekopie. */
export interface NoteRefDto {
    art: NoteRefArt;
    id: number | string;
    label?: string | null;
}

export interface NoteDto {
    id: number;
    text: string;
    author: string;
    timestamp: string;
    mentions?: string[];
    /** Threading: Antwort auf diese Eltern-Notiz; null/undefined = Top-Level-Notiz. */
    parentId?: number | null;
    /** #-Hashtag (T4-PR2): bezogenes Risiko/Vertrag + aufgelöstes Label (Badge + Navigation). */
    riskObjectId?: number | null;
    riskLabel?: string | null;
    contractId?: number | null;
    contractLabel?: string | null;
    /** Wann der Text zuletzt geändert wurde; null = unverändert. */
    updatedAt?: string | null;
    /**
     * Bezug auf einen Verlaufseintrag — dann ist die Notiz ein Kommentar zu
     * jener Mail / jenem Anruf / jenem Dokument / jenem Termin. Höchstens
     * einer ist gesetzt.
     */
    mailLinkId?: number | null;
    callLogId?: number | null;
    documentId?: number | null;
    /** Kommentar an einem Termin (PO 20.09.2026). */
    terminId?: number | null;
    /**
     * Kommentar an einem Mail-ENTWURF (User 22.10.2026): dessen Graph-Id.
     * Beim Senden aus KMVS wandert er an die gesendete Mail (`mailLinkId`).
     */
    entwurfId?: string | null;
    /** `'text'` = Klartext, `'html'` = Blockeditor-Inhalt (PO 08.09.2026). */
    format?: "text" | "html";
    /** Verweise des Blocks — Liste plus die alten Skalar-Bezüge. */
    refs?: NoteRefDto[];
    /** Manuelle Reihenfolge; null/undefined = nach Zeit. */
    sortIndex?: number | null;
    /** Alle, die am Block geschrieben haben (Anzeige im Blockkopf). */
    editors?: string[];
    /** Gezogene Breite in Pixel (PO 09.09.2026); null = mit dem Inhalt wachsen. */
    breite?: number | null;
    /**
     * Der Haken am KOMMENTAR (PO 18.09.2026); null = offen. Der Haken am To-do
     * erledigt den ganzen Arbeitsstrang, dieser nur die eine Bitte.
     */
    erledigtAm?: string | null;
    erledigtVon?: string | null;
    /**
     * Die markierte Stelle im Notiztext, an der dieser Kommentar hängt
     * (PO 18.09.2026); null = er gehört zur ganzen Notiz.
     */
    anker?: string | null;
}

export interface TwvItemDto {
    kind: "vorgang" | "todo";
    id: number;
    titel: string;
    twv: string;
    vorgangId: number | null;
}

/** Kunde summary attached to Vorgänge/Todos for the list columns. The backend
 *  resolves `boTeam` / `boUser` / `foUser` display names (see kunde-enrichment.ts). */
export interface KundeSummary {
    id: number;
    lastname?: string;
    firstName?: string;
    boType?: "NONE" | "USER" | "TEAM";
    boTeam?: { id: string; name?: string | null } | null;
    boUser?: { userId: string; kuerzel?: string | null; displayName: string } | null;
    /** Frontoffice-User (Kontaktverantwortlicher) — für den FO-Kürzelkreis der KV-Anzeige. */
    foUser?: { userId: string; kuerzel?: string | null; displayName: string } | null;
    /**
     * Rohfelder der Kontaktverantwortung. `GET /vorgang` liefert den ganzen
     * Kunden-Kontakt mit; der Bereich „Team" (T-395) liest daraus alle FO
     * (`foUserIds`, Mehrfach-FO) und den namentlichen BO.
     */
    foUserId?: string | null;
    foUserIds?: string[] | null;
    boUserId?: string | null;
    boTeamId?: string | null;
    /** Firma/Person + Kunde/Interessent — vom Todo-Detail-Endpoint mitgeliefert. */
    type?: { id: number; type?: string } | null;
    status?: { id: number; status?: string } | null;
}

/** Risiko-/Vertrag-Summaries der parallelen Verknüpfungs-Slots (Detail-Remake,
 *  Backend link-summaries.ts). */
export interface RisikoSummary {
    id: number;
    name?: string | null;
    riskType?: string | null;
}

export interface VertragSummary {
    id: number;
    contractNbr?: string | null;
    branch?: string | null;
}

/** Antwort von `POST todo/vorgang/:id/todo-form` (siehe `stelleTodoFormHer`). */
export interface TodoFormErgebnis {
    vorgangId: number;
    aktion: "keine" | "zurueckgefaltet" | "erstes-todo";
    grund?: string;
    todoId?: number;
    umgehaengt?: number;
}

export interface TodoDto {
    id: number;
    titel: string;
    ersteller: string;
    /** User-id des Anlegenden (aus AP1, Vorbild Contract.createdByUserId). */
    createdByUserId?: string | null;
    hauptverantwortlich: string[];
    /** @deprecated no longer shown in UI */
    nebenverantwortlich?: string;
    /** @deprecated kept for back-compat with old rows; not rendered. */
    dringlichkeit?: number;
    status: string;
    twv: string;
    twvZielgruppe?: TwvZielgruppe | null;
    frist: string;
    /** Aufgabenart — what the work *is* (template key). */
    art?: string;
    /** Zuweisung — which business object this todo belongs to. */
    linkedEntityType?: LinkedEntityType | null;
    linkedEntityId?: number | null;
    /** Parallele Verknüpfungs-Slots (Detail-Remake): Kontakt + Risiko + Vertrag
     *  gleichzeitig; das Backend spiegelt KUNDE-Links in beide Richtungen. */
    contactId?: number | null;
    contactIds?: number[];
    riskObjectId?: number | null;
    contractId?: number | null;
    /** Mehrfach-Zuordnungen (SP3) — Position 0 spiegelt riskObjectId/contractId. */
    riskObjectIds?: number[];
    contractIds?: number[];
    /** Aufgelöste Anzeige-Summaries der Slots (nur Detail-GET). */
    risiko?: RisikoSummary | null;
    vertrag?: VertragSummary | null;
    /** Anzeige-Summaries ALLER Mehrfach-Zuordnungen (nur Detail-GET, SP3). */
    risiken?: RisikoSummary[];
    vertraege?: VertragSummary[];
    kontakte?: KundeSummary[];
    beschreibung?: string;
    /** Passive Beobachter/CC — speist den zweiseitigen Info-Cluster (Info erwartet / Info geben). */
    zuInformierende?: string[];
    /** Active worker list. */
    bearbeiter?: string[];
    /** "Austausch mit [Person] am [Datum]". */
    austauschMit?: string;
    austauschAm?: string;
    vorgangId?: number;
    /** Eltern-Vorgang (Detail-GET lädt die Relation mit) — seit der
     *  „Nur Vorgänge"-Invariante (SP2) hat jedes To-do einen Vorgang. */
    vorgang?: { id: number } | null;
    /** Erstellzeitpunkt (ISO) — Sortierung „neueste zuerst". */
    createdAt?: string;
    /** Resolved Kunde (parent Vorgang or linkedEntityType=KUNDE) — list columns + detail header. */
    kunde?: KundeSummary | null;
    notes: NoteDto[];
    /** Legacy M:M relations — preserved on existing rows; new code uses linkedEntity*. */
    assignedContacts?: { id: number; lastname?: string; firstName?: string }[];
    assignedContracts?: { id: number; contractNumber?: string }[];
    assignedRisks?: { id: number; description?: string; objectName?: string }[];
}

/* ────────── Aktivitäts-Feed (AP9 / D16) ────────── */

/** Ein Eintrag im kombinierten Vorgangs-Aktivitäts-Feed (Mail oder Stammdaten-Änderung). */
export type ActivityItem =
    | {
          kind: "mail";
          /** ISO-Zeitstempel — Sortierung & Datums-Gruppierung. */
          at: string;
          title: string;
          sub: string | null;
          mailbox: string;
          messageId: string;
          conversationId: string | null;
          preview: string | null;
      }
    | {
          kind: "change";
          at: string;
          entity: { type: "Contact" | "Contract" | "RiskObject"; id: number };
          action: "INSERT" | "UPDATE" | "DELETE";
          fieldSummary: string;
          userId: string | null;
          /** Roh-Diff (alt→neu) je Feld — im Frontend als Expander. */
          diff: Record<string, { old: unknown; new: unknown }>;
      };

export interface ActivityResult {
    items: ActivityItem[];
    total: number;
    limit: number;
    offset: number;
}

/* ── Aktivitäts-Feed V2 (Detail-Remake SP6/SP7) ──────────────────────────
 * Menschenlesbare Änderungen NUR von Vertrag/Risiko (+ Dokument-Uploads);
 * eigene Typen, damit die (entroutete) Alt-Komponente unberührt bleibt. */

export interface ActivityEntityRef {
    type: "Contract" | "RiskObject";
    id: number;
    label?: string | null;
}

export type ActivityItemV2 =
    | {
          kind: "mail";
          at: string;
          title: string;
          sub: string | null;
          mailbox: string;
          messageId: string;
          conversationId: string | null;
          preview: string | null;
      }
    | {
          kind: "change";
          at: string;
          entity: ActivityEntityRef;
          action: "INSERT" | "UPDATE" | "DELETE";
          /** Menschlich lesbarer Satz („Vertrag V-2024-001 angelegt"). */
          title: string;
          fieldSummary: string;
          userId: string | null;
          diff: Record<string, { old: unknown; new: unknown }>;
      }
    | {
          kind: "dokument";
          at: string;
          title: string;
          fileName: string;
          category: string | null;
          entity: ActivityEntityRef | null;
      };

export interface ActivityResultV2 {
    items: ActivityItemV2[];
    total: number;
    limit: number;
    offset: number;
}

/** Ein übersprungener Vorgang einer Sammelaktion (T-196) samt Grund. */
export type SammelSkip = { id: number; reason: "not_found" | "forbidden" | "fehler" };

export interface VorgangDto {
    id: number;
    titel: string;
    /**
     * Fachliche Vorgangsnummer `<Kundennummer>-<lfd. Nr. pro Kunde>` (PO
     * 26.07.2026), z. B. „4711-2". Optional: Bestandsvorgänge ohne auflösbaren
     * Kunden haben keine — die Anzeige fällt dann auf die technische `id` zurück.
     */
    vorgangNbr?: string | null;
    frist: string;
    twv: string;
    /** Optionale Uhrzeit zur TWV ('HH:MM') — reines Anzeige-/Sortierfeld neben `twv`. */
    twvUhrzeit?: string | null;
    bearbeiter: any[];
    fortschrittErledigt: number;
    fortschrittGesamt: number;
    kunde?: KundeSummary | null;
    /** Führender Kontakt zuerst, danach zusätzliche fachliche Kontakte. */
    contactIds?: number[];
    kontakte?: KundeSummary[];
    /** Parallele Verknüpfungs-Slots (Detail-Remake). */
    riskObjectId?: number | null;
    contractId?: number | null;
    /** Mehrfach-Zuordnungen (SP3) — Position 0 spiegelt riskObjectId/contractId. */
    riskObjectIds?: number[];
    contractIds?: number[];
    /** Manueller Status — nur wirksam, wenn der Vorgang keine aktiven Todos hat. */
    statusManuell?: string | null;
    /** Beteiligte auf Vorgang-Ebene (Create-Rework CR-SP1). */
    hauptverantwortlich?: string[];
    zuInformierende?: string[];
    /** User-id des Anlegenden — Edit-Recht-Prüfung (Ersteller darf bearbeiten, SP4);
     *  zugleich Quelle der „Ersteller"-Anzeige in der Personen-Zeile (SP6). */
    createdByUserId?: string | null;
    /** Record-Level-Owner (FO) — Fallback für die „Ersteller"-Anzeige, falls
     *  createdByUserId (Alt-Bestand) fehlt. */
    ownerUserId?: string | null;
    /**
     * E07/I2: persönlicher Vorgang — ohne Kontakt-, Risiko- und Vertragsbezug,
     * ausschließlich für seinen Ersteller sichtbar. Serverseitig gesetzt und
     * nach der Anlage unveränderlich; hier nur Anzeige.
     */
    persoenlich?: boolean;
    /** Aus einer alten Kundenakte übernommen (nur vom Import gesetzt). */
    importiert?: boolean;
    /** Record-Level-Sichtbarkeit ('PRIVATE' | 'TEAM' | 'TENANT') — nur Anzeige. */
    accessLevel?: string | null;
    /** Serverseitig abgeleiteter Status (deriveVorgangStatus) — Single Source of Truth. */
    status?: string;
    /** Aufgelöste Anzeige-Summaries der Slots (nur Detail-GET). */
    risiko?: RisikoSummary | null;
    vertrag?: VertragSummary | null;
    /** Anzeige-Summaries ALLER Mehrfach-Zuordnungen (nur Detail-GET, SP3). */
    risiken?: RisikoSummary[];
    vertraege?: VertragSummary[];
    /** Vorgangsart — NKG | JG | GAB | Sonstige (Default), siehe data/vorgangArt.ts. Bestands-Werte („Allgemein", „SG-Service", „Rückruf") bleiben als Freitext erhalten. */
    vorgangsart?: string | null;
    /** Vorlage key (e.g. JAHRESGESPRAECH). */
    vorlageKey?: string;
    beschreibung?: string;
    /**
     * Was am Ende herauskam (PO 09.09.2026) — beim Erledigen abgefragt,
     * überspringbar. Nicht zu verwechseln mit `beschreibung`: die ist der
     * Untertitel aus der Anlage und sagt, worum es geht.
     */
    zusammenfassung?: string | null;
    /** Erstellzeitpunkt (ISO) — Sortierung „neueste zuerst". */
    createdAt?: string;
    /** Serverseitig aggregiert (SP2): eigene userId in einer Vorgang- ODER Todo-Notiz @-erwähnt. */
    mentionedMe?: boolean;
    /** Serverseitig gesetzt (SP3, A10): dieser Vorgang gehört einer Firma, bei der
     *  der geöffnete Kontakt Ansprechpartner ist — Badge „über <Firma>". */
    viaCompany?: { id: number | null; name: string | null } | null;
    /**
     * Serverseitig gesetzt (PO 08.09.2026, nur mit `include=indirekt`): dieser
     * Vorgang gehört nicht dem geöffneten Kontakt, sondern einem Kontakt aus
     * seinem Umfeld. Fehlt das Feld, ist es ein EIGENER Vorgang.
     */
    bezug?: {
        ebene: "firma" | "verbund" | "haushalt";
        kontaktId: number;
        kontaktName: string | null;
    } | null;
    /** Papierkorb: Löschzeitpunkt (ISO) — nur in der Papierkorb-Liste gesetzt,
     *  reguläre Endpoints liefern ausschließlich aktive Vorgänge. */
    deleted?: string | null;
    /**
     * Die OFFENEN Austausche am Vorgang und seinen To-dos zusammengefasst
     * (T-302, seit 01.11.2026 mit allen Teilnehmern) — nur im Feed der
     * Vorgangsliste gesetzt, für die Spalte „Austausch". `null` = keiner offen;
     * fehlt das Feld, weiß man es nicht.
     */
    offenerAustausch?: OffenerAustauschDto | null;
    todos: TodoDto[];
}

/* ────────── API calls ────────── */

async function request<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        headers: { "Content-Type": "application/json" },
        ...options,
    });
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`API error ${res.status}: ${text}`);
    }
    if (res.status === 204) return undefined as T;
    // Defense-in-depth: manche void-Endpoints (z. B. DELETE) liefern 200 mit
    // LEEREM Body — res.json() würde darauf mit SyntaxError werfen und die
    // erfolgreiche Aktion fälschlich als Fehler melden ("Löschen fehlgeschlagen").
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

/* SWR cache keys. Mutations call `invalidateByPrefix("vorgang.")` /
 * `invalidateByPrefix("todo.")` so listing endpoints refresh on the next read. */
const KEY_VORGANG_ALL = "vorgang.all";
const KEY_TODO_ALL = "todo.all";
const TTL_LIST_MS = 30_000; // 30s — sufficient for an interactive demo / dev backend
const TTL_ITEM_MS = 60_000; // 60s — single-entity GETs change less often

export const vorgangService = {
    /**
     * Die To-dos EINES Vorgangs für das Aufklappen im Mail-Picker
     * (PO 30.08.2026). Schlank — id, Betreff, Status.
     *
     * Ein fremder oder nicht lesbarer Vorgang liefert serverseitig eine LEERE
     * Liste, keinen Fehler: die Auswahlliste soll beim Aufklappen nicht
     * abbrechen.
     */
    async pickerTodos(vorgangId: number): Promise<VorgangPickerTodo[]> {
        const res = await fetch(apiUrl(`vorgang/${vorgangId}/picker-todos`));
        if (!res.ok) throw new Error(`Picker-Todos fehlgeschlagen: ${res.status}`);
        return res.json();
    },

    /* Verknüpfte To-dos (PO 05.09.2026: „in beiden Vorgängen zeigen") */

    /**
     * Die To-dos, die dieser Vorgang zusätzlich anzeigt. Das Backend filtert
     * dabei auf das, was der Nutzer im HEIMAT-Vorgang lesen darf — die Liste
     * kann deshalb kürzer sein als die Zahl der Verknüpfungen.
     */
    async verknuepfteTodos(vorgangId: number): Promise<VerknuepftesTodoDto[]> {
        return request<VerknuepftesTodoDto[]>(apiUrl(`vorgang/${vorgangId}/verknuepfte-todos`));
    },

    async verknuepfeTodo(vorgangId: number, todoId: number): Promise<void> {
        await request(apiUrl(`vorgang/${vorgangId}/verknuepfte-todos`), {
            method: "POST",
            body: JSON.stringify({ todoId }),
        });
        invalidateByPrefix("vorgang.");
    },

    async loeseTodoVerknuepfung(vorgangId: number, todoId: number): Promise<void> {
        await request(apiUrl(`vorgang/${vorgangId}/verknuepfte-todos/${todoId}`), {
            method: "DELETE",
        });
        invalidateByPrefix("vorgang.");
    },

    /* Verknüpfte Dokumente (PO 22.09.2026: „ein Dokument, mehrere Vorgänge") */

    /**
     * Hängt Dokumente an diesen Vorgang — aus der Kundenakte oder aus einem
     * anderen Vorgang. Sie bleiben, wo sie zu Hause sind; dieser Vorgang zeigt
     * sie zusätzlich an. `todoId` = `null`/fehlend heißt „am Vorgang selbst".
     *
     * Ein erneuter Aufruf mit anderem `todoId` VERSCHIEBT das Dokument
     * innerhalb des Vorgangs. Dokumente, die hier ohnehin zu Hause sind,
     * kommen als `schonDabei` zurück — kein Fehler, sondern die Antwort auf
     * eine Auswahl, in die eines davon mitgerutscht ist.
     */
    async verknuepfeDokumente(
        vorgangId: number,
        documentIds: number[],
        todoId?: number | null,
    ): Promise<{ verknuepft: number[]; schonDabei: number[] }> {
        const antwort = await request<{ verknuepft: number[]; schonDabei: number[] }>(
            apiUrl(`vorgang/${vorgangId}/dokumente`),
            {
                method: "POST",
                body: JSON.stringify({ documentIds, ...(todoId != null ? { todoId } : {}) }),
            },
        );
        invalidateByPrefix("vorgang.");
        return antwort;
    },

    /**
     * Kontakte als WEITERE Kontakte in den Vorgang aufnehmen (F2 beim
     * Zuordnen eines Anrufs). Anhängen, nie ersetzen; der Kunde bleibt vorn.
     */
    async kontakteAufnehmen(vorgangId: number, contactIds: number[]): Promise<void> {
        await request(apiUrl(`vorgang/${vorgangId}/kontakte`), {
            method: "POST",
            body: JSON.stringify({ contactIds }),
        });
        invalidateByPrefix("vorgang.");
    },

    /** Nimmt das Dokument aus DIESEM Vorgang. Es bleibt in seiner Heimat. */
    async loeseDokument(vorgangId: number, documentId: number): Promise<void> {
        await request(apiUrl(`vorgang/${vorgangId}/dokumente/${documentId}`), {
            method: "DELETE",
        });
        invalidateByPrefix("vorgang.");
    },

    /* Vorgänge */

    /**
     * `opts.onUpdate` is invoked when the cached list is stale and a background
     * refetch completes. Pass `force: true` after a mutation to bypass the cache.
     */
    async getAllVorgaenge(
        opts: Partial<CachedOpts<VorgangDto[]>> = {},
    ): Promise<VorgangDto[]> {
        return cached(
            KEY_VORGANG_ALL,
            () => request<VorgangDto[]>(apiUrl("vorgang")),
            { ttlMs: TTL_LIST_MS, ...opts },
        );
    },

    /**
     * Paginierter Feed (T7): Vorgänge + standalone Todos, createdAt-DESC, mit
     * serverseitigem Scope (alle/team/persoenlich). `total` aus X-Total-Count.
     * Bewusst NICHT SWR-gecached (Offset-Paging mit eigenem Hook-State).
     *
     * `sort` sortiert SERVERSEITIG über den gesamten gefilterten Bestand — nicht
     * nur über die bereits geladenen Seiten. Der Server nimmt nur Felder aus
     * seiner Whitelist an und fällt sonst auf „neueste zuerst" zurück.
     */
    async getFeedPage(
        scope: "alle" | "team" | "persoenlich",
        offset: number,
        limit: number,
        signal?: AbortSignal,
        cluster?: string,
        q?: string,
        filters?: FeedFilterParams,
        sort?: { field: string; direction: "asc" | "desc" } | null,
    ): Promise<{ items: VorgangFeedItem[]; total: number | null }> {
        const params: Record<string, string | number> = { scope, offset, limit };
        if (cluster) params.cluster = cluster;
        if (q && q.trim()) params.q = q.trim();
        if (sort?.field) {
            params.sort = sort.field;
            params.direction = sort.direction === "desc" ? "desc" : "asc";
        }
        if (filters) {
            if (filters.status) params.status = filters.status;
            if (filters.titel) params.titel = filters.titel;
            if (filters.kontakt) params.kontakt = filters.kontakt;
            if (filters.boTeamIds?.length) params.boTeamIds = filters.boTeamIds.join(",");
            if (filters.foUserIds?.length) params.foUserIds = filters.foUserIds.join(",");
            if (filters.beteiligt?.length) params.beteiligt = filters.beteiligt.join(",");
            if (filters.beteiligt?.length && filters.beteiligtRollen?.length) {
                params.beteiligtRollen = filters.beteiligtRollen.join(",");
            }
            if (filters.fristVon) params.fristVon = filters.fristVon;
            if (filters.fristBis) params.fristBis = filters.fristBis;
            if (filters.twvVon) params.twvVon = filters.twvVon;
            if (filters.twvBis) params.twvBis = filters.twvBis;
            if (filters.ersteller?.length) params.ersteller = filters.ersteller.join(",");
            if (filters.austausch) params.austausch = filters.austausch;
            if (filters.austausch !== "ohne") {
                if (filters.austauschMit?.length) params.austauschMit = filters.austauschMit.join(",");
                if (filters.austauschExtern) params.austauschExtern = filters.austauschExtern;
            }
        }
        const { data, total } = await apiFetch<VorgangFeedItem[]>("vorgang/feed", {
            params,
            signal,
        });
        return { items: data ?? [], total };
    },

    /**
     * Gestufte, serverseitig gesuchte und paginierte Vorgangs-Auswahl (D1–D3).
     * Bewusst NICHT SWR-gecached (Offset-Paging mit eigenem Hook-State), analog
     * zu `getFeedPage`.
     *
     * `contactId` steuert NUR die Rangfolge (Stufe 1) — nie die Sichtbarkeit;
     * ohne Kontakt-Zugriff entfällt Stufe 1 serverseitig still.
     */
    async getPickerPage(args: {
        contactId?: number | null;
        q?: string;
        scope?: "all" | "contact";
        /** Erledigte Vorgänge ausblenden (Mail-Zuweisung). */
        nurOffene?: boolean;
        /** T-55: bereits verknüpfter Vorgang — der Server stellt ihn ganz oben hin. */
        aktuelleVorgangId?: number | null;
        /** T-56: Bereich Alle/Team/Eigene wie auf der Vorgänge-Seite; „alle“ wird nicht gesendet. */
        bereich?: "alle" | "team" | "persoenlich";
        /**
         * T-348: nur die eigenen persönlichen Vorgänge (ohne Kontakt, Eigentümer
         * = ich). Der Server ignoriert dann den Bereich.
         */
        art?: "persoenlich" | null;
        limit: number;
        offset: number;
        signal?: AbortSignal;
    }): Promise<{ items: VorgangPickerItem[]; total: number | null }> {
        const params: Record<string, string | number> = { limit: args.limit, offset: args.offset };
        if (args.nurOffene) params.nurOffene = 1;
        if (args.aktuelleVorgangId != null && args.aktuelleVorgangId > 0) {
            params.aktuell = args.aktuelleVorgangId;
        }
        if (args.bereich && args.bereich !== "alle") params.bereich = args.bereich;
        if (args.art) params.art = args.art;
        if (args.contactId != null) params.contactId = args.contactId;
        if (args.q && args.q.trim()) params.q = args.q.trim();
        if (args.scope && args.scope !== "all") params.scope = args.scope;
        const { data, total } = await apiFetch<VorgangPickerItem[]>("vorgang/picker", {
            params,
            signal: args.signal,
        });
        return { items: data ?? [], total };
    },

    /** Cluster-Zähler je Pill (scope + optionale Suche) — getrennt vom paginierten Feed. */
    async getFeedCounts(
        scope: "alle" | "team" | "persoenlich",
        q?: string,
        signal?: AbortSignal,
    ): Promise<Record<string, number>> {
        const params: Record<string, string> = { scope };
        if (q && q.trim()) params.q = q.trim();
        const { data } = await apiFetch<Record<string, number>>("vorgang/feed/counts", {
            params,
            signal,
        });
        return data ?? {};
    },

    async getVorgang(id: number, opts: Partial<CachedOpts<VorgangDto>> = {}): Promise<VorgangDto> {
        return cached(
            `vorgang.${id}`,
            () => request<VorgangDto>(apiUrl(`vorgang/${id}`)),
            { ttlMs: TTL_ITEM_MS, ...opts },
        );
    },

    /** Vorgänge for a single contact (incl. their todos) — backend filters by kundeId. */
    /**
     * Vorgänge eines Kontakts. Mit `indirekt` kommen die Vorgänge seines
     * Umfelds dazu (Firma, Unternehmensverbund, Haushalt), je Eintrag über
     * `bezug` beschriftet — das braucht der Vorgänge-Reiter der Kontaktakte.
     *
     * EIGENER Cache-Schlüssel je Variante: sonst bekäme der Archiv-Reiter die
     * Umfeld-Liste aus dem Cache serviert und zeigte fremde Vorgänge.
     */
    async getVorgaengeByContact(
        contactId: number,
        opts: Partial<CachedOpts<VorgangDto[]>> & { indirekt?: boolean } = {},
    ): Promise<VorgangDto[]> {
        const { indirekt, ...cacheOpts } = opts;
        return cached(
            `vorgang.byContact.${contactId}${indirekt ? ".indirekt" : ""}`,
            () =>
                request<VorgangDto[]>(
                    apiUrl("vorgang", indirekt ? { contactId, include: "indirekt" } : { contactId }),
                ),
            { ttlMs: TTL_LIST_MS, ...cacheOpts },
        );
    },

    async createVorgang(data: Partial<Omit<VorgangDto, "id" | "todos">>): Promise<VorgangDto> {
        const created = await request<VorgangDto>(apiUrl("vorgang"), {
            method: "POST",
            body: JSON.stringify(data),
        });
        invalidateByPrefix("vorgang.");
        return created;
    },

    async updateVorgang(id: number, data: Partial<Omit<VorgangDto, "id" | "todos">>): Promise<VorgangDto> {
        const updated = await request<VorgangDto>(apiUrl(`vorgang/${id}`), {
            method: "PUT",
            body: JSON.stringify(data),
        });
        invalidate(`vorgang.${id}`);
        invalidate(KEY_VORGANG_ALL);
        return updated;
    },

    /**
     * F4-Härtung (Befund 2): Beteiligte (bearbeiter/hauptverantwortlich) laufen
     * über einen dedizierten, write-gateten Endpoint — der generische PUT
     * ignoriert diese Autorisierungsfelder serverseitig. Nur die tatsächlich
     * geänderten Felder mitschicken.
     */
    async updateVorgangBeteiligte(
        id: number,
        data: { bearbeiter?: string[]; hauptverantwortlich?: string[] },
    ): Promise<VorgangDto> {
        const updated = await request<VorgangDto>(apiUrl(`vorgang/${id}/beteiligte`), {
            method: "PUT",
            body: JSON.stringify(data),
        });
        invalidate(`vorgang.${id}`);
        invalidate(KEY_VORGANG_ALL);
        return updated;
    },

    /**
     * Self-Enroll: Der aufrufende User trägt sich selbst als „weiteren
     * Bearbeiter" ein und erhält damit (bei nicht-privaten Vorgängen)
     * Bearbeitungsrechte. Read-gateter Endpoint (nur wer den Vorgang sehen darf);
     * fügt ausschließlich einen selbst hinzu. Liefert den aktualisierten Vorgang.
     */
    async selfEnrollBearbeiter(id: number): Promise<VorgangDto> {
        const updated = await request<VorgangDto>(apiUrl(`vorgang/${id}/beteiligte/self`), {
            method: "POST",
        });
        invalidate(`vorgang.${id}`);
        invalidate(KEY_VORGANG_ALL);
        return updated;
    },

    /** Papierkorb (Soft-Delete): Vorgang + seine To-dos werden markiert, nicht gelöscht. */
    async deleteVorgang(id: number): Promise<void> {
        await request<void>(apiUrl(`vorgang/${id}`), { method: "DELETE" });
        invalidate(`vorgang.${id}`);
        invalidate(KEY_VORGANG_ALL);
    },

    /**
     * Sammelaktion der Vorgangsliste (T-196): mehrere Vorgänge auf einmal
     * erledigen bzw. wieder öffnen — höchstens 200. Recht prüft der Server je
     * Vorgang; nicht erlaubte/unsichtbare stehen in `skipped`.
     */
    async setzeStatusMehrfach(
        ids: number[],
        status: "Offen" | "Erledigt",
    ): Promise<{ geaendert: number[]; skipped: SammelSkip[] }> {
        const res = await request<{ geaendert: number[]; skipped: SammelSkip[] }>(apiUrl("vorgang/sammel/status"), {
            method: "POST",
            body: JSON.stringify({ ids, status }),
        });
        for (const id of res.geaendert ?? []) invalidate(`vorgang.${id}`);
        invalidate(KEY_VORGANG_ALL);
        return res;
    },

    /** Sammelaktion (T-196): mehrere Vorgänge samt To-dos in den Papierkorb — höchstens 200. */
    async loescheMehrfach(ids: number[]): Promise<{ geloescht: number[]; skipped: SammelSkip[] }> {
        const res = await request<{ geloescht: number[]; skipped: SammelSkip[] }>(apiUrl("vorgang/sammel/loeschen"), {
            method: "POST",
            body: JSON.stringify({ ids }),
        });
        for (const id of res.geloescht ?? []) invalidate(`vorgang.${id}`);
        invalidate(KEY_VORGANG_ALL);
        return res;
    },

    /** Soft-gelöschte Vorgänge, die der Aufrufer wiederherstellen dürfte. */
    async getDeletedVorgaenge(): Promise<VorgangDto[]> {
        return request<VorgangDto[]>(apiUrl("vorgang/papierkorb/liste"));
    },

    /** Wiederherstellen aus dem Papierkorb (idempotent). */
    async restoreVorgang(id: number): Promise<void> {
        await request<void>(apiUrl(`vorgang/${id}/restore`), { method: "POST" });
        invalidate(`vorgang.${id}`);
        invalidate(KEY_VORGANG_ALL);
    },

    /* Todos */
    async getAllTodos(
        opts: Partial<CachedOpts<TodoDto[]>> = {},
        light = false,
    ): Promise<TodoDto[]> {
        // `light` (Dashboard): ohne notes/assigned*-Relationen — deutlich schneller.
        const key = `${KEY_TODO_ALL}${light ? ".light" : ""}`;
        const params: Record<string, string> = {};
        if (light) params.light = "1";
        return cached(
            key,
            () => request<TodoDto[]>(apiUrl("todo", Object.keys(params).length ? params : undefined)),
            { ttlMs: TTL_LIST_MS, ...opts },
        );
    },

    async getTodo(id: number, opts: Partial<CachedOpts<TodoDto>> = {}): Promise<TodoDto> {
        return cached(
            `todo.${id}`,
            () => request<TodoDto>(apiUrl(`todo/${id}`)),
            { ttlMs: TTL_ITEM_MS, ...opts },
        );
    },

    /** Todos linked to a given business object (e.g. KUNDE → a contact). */
    async getTodosByLinked(
        type: LinkedEntityType,
        id: number,
        opts: Partial<CachedOpts<TodoDto[]>> = {},
    ): Promise<TodoDto[]> {
        return cached(
            `todo.byLinked.${type}.${id}`,
            () => request<TodoDto[]>(apiUrl(`todo/by-linked/${type}/${id}`)),
            { ttlMs: TTL_LIST_MS, ...opts },
        );
    },

    async createTodo(data: Partial<Omit<TodoDto, "id" | "notes">> & {
        vorgangId?: number;
        /**
         * @-Erwähnungen aus dem Beschreibungs-/Notizfeld. Der Server legt die
         * Beschreibung als erste To-do-Notiz im Notizen-Chat des Vorgangs ab und
         * benachrichtigt die Erwähnten (PO 02.08.2026).
         */
        beschreibungMentions?: string[];
    }): Promise<TodoDto> {
        const created = await request<TodoDto>(apiUrl("todo"), {
            method: "POST",
            body: JSON.stringify(data),
        });
        invalidateByPrefix("todo.");
        // A new todo may belong to a Vorgang — invalidate Vorgang caches too
        // so the progress bar / sub-todo list rerenders correctly.
        invalidateByPrefix("vorgang.");
        return created;
    },

    /**
     * DER VORGANGSSTRANG WIRD DAS ERSTE TO-DO (PO 16.09.2026).
     *
     * Für Vorgänge, deren To-dos ohne die Übernahme entstanden sind (V2):
     * Der Server legt ein erstes To-do mit dem Betreff des Vorgangs an und
     * zieht alles bisher direkt am Vorgang Hängende hinein — in EINER
     * Transaktion, nicht Zeile für Zeile aus dem Browser.
     *
     * Idempotent: Gibt es nichts zu übernehmen, kommt `angelegt: false`
     * zurück und nichts ist passiert.
     */
    async uebernimmVorgangsstrang(
        vorgangId: number,
    ): Promise<{ todoId: number | null; umgehaengt: number; angelegt: boolean }> {
        const ergebnis = await request<{ todoId: number | null; umgehaengt: number; angelegt: boolean }>(
            apiUrl(`todo/vorgang/${vorgangId}/uebernahme`),
            { method: "POST" },
        );
        if (ergebnis.angelegt) {
            invalidateByPrefix("todo.");
            invalidateByPrefix("vorgang.");
        }
        return ergebnis;
    },

    /**
     * NIE GENAU EIN TO-DO (User 03.11.2026): Steht im Vorgang genau ein To-do,
     * wird es der Vorgang (oder am Vorgang hängendes Eigenes wird ein zweites).
     * Die Server-Wege rufen das selbst; die Erstell-Wege im Browser rufen es,
     * sobald sie fertig angehängt haben. Idempotent.
     */
    async stelleTodoFormHer(vorgangId: number): Promise<TodoFormErgebnis> {
        const ergebnis = await request<TodoFormErgebnis>(apiUrl(`todo/vorgang/${vorgangId}/todo-form`), {
            method: "POST",
        });
        if (ergebnis.aktion !== "keine") {
            invalidateByPrefix("todo.");
            invalidateByPrefix("vorgang.");
        }
        return ergebnis;
    },

    async updateTodoStatus(id: number, status: string): Promise<TodoDto> {
        const updated = await request<TodoDto>(apiUrl(`todo/${id}`), {
            method: "PUT",
            body: JSON.stringify({ status }),
        });
        // Prefix statt Exakt-Keys (wie createTodo): sonst bleiben die
        // per-Kontakt-Listen `todo.byLinked.*` (Vorgänge-Tab) + die Draft-
        // Varianten `todo.all.*` bis zum TTL stale und zeigen den alten Status.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return updated;
    },

    async updateTodo(id: number, data: Partial<Omit<TodoDto, "id">>): Promise<TodoDto> {
        const updated = await request<TodoDto>(apiUrl(`todo/${id}`), {
            method: "PUT",
            body: JSON.stringify(data),
        });
        // Prefix statt Exakt-Keys (wie createTodo): sonst bleiben die
        // per-Kontakt-Listen `todo.byLinked.*` (Vorgänge-Tab) + die Draft-
        // Varianten `todo.all.*` bis zum TTL stale.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return updated;
    },

    /**
     * Ein To-do in einen ANDEREN Vorgang umhängen (PO 21.09.2026).
     *
     * Derselbe Endpunkt wie `updateTodo`, aber mit eigenem Namen: Ein Move ist
     * keine Feldänderung. Serverseitig prüft er ZWEI Vorgänge (`assertTodoAccess`
     * für den alten, `assertZielVorgangLesbar` für den neuen) und zieht den
     * Fortschritt beider nach — `updateTodo` mit `{ vorgangId }` aufzurufen
     * sähe an der Aufrufstelle wie ein belangloser Patch aus.
     *
     * Alles, was am To-do hängt — Mails, Dokumente, Anrufe, Termine, Notizen —
     * wandert MIT. Dass es das tut, ist allerdings Arbeit des Servers und war
     * bis zum 22.09.2026 nicht wahr: Hier stand die Annahme, diese Zeilen
     * hingen „an der `todoId`, nicht am Vorgang". Sie tragen BEIDE Spalten,
     * und die zweite blieb stehen — das To-do kam drüben weitgehend leer an.
     * Der Umzug steht jetzt in `TodoService.zieheStrangZumTodo`.
     */
    async verschiebeTodo(todoId: number, zielVorgangId: number): Promise<TodoDto> {
        const verschoben = await request<TodoDto>(apiUrl(`todo/${todoId}`), {
            method: "PUT",
            body: JSON.stringify({ vorgangId: zielVorgangId }),
        });
        // Zwei Vorgänge sind betroffen, der alte und der neue — Prefix statt
        // Exakt-Keys, sonst zeigt die Herkunftsseite das To-do weiter an.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return verschoben;
    },

    /**
     * Einen Vorgang OHNE eigene To-dos als To-do in einen anderen Vorgang
     * verschieben (PO 21.09.2026).
     *
     * Serverseitig in EINER Transaktion: neues To-do im Ziel, alles am
     * Quellvorgang Hängende zieht hinein, der leere Quellvorgang geht in den
     * Papierkorb. Er ist von dort wiederherstellbar — sein Inhalt kommt dabei
     * aber nicht mit zurück, der hängt dann am neuen To-do.
     *
     * Hat der Vorgang To-dos, lehnt der Server mit 400 ab: Dann ist er mehr
     * als ein Arbeitsschritt, und der richtige Weg heißt `verschiebeTodo` je
     * To-do.
     */
    async verschiebeVorgangAlsTodo(
        vorgangId: number,
        zielVorgangId: number,
    ): Promise<{ todoId: number; umgehaengt: number }> {
        const ergebnis = await request<{ todoId: number; umgehaengt: number }>(
            apiUrl(`todo/vorgang/${vorgangId}/als-todo-verschieben`),
            { method: "POST", body: JSON.stringify({ zielVorgangId }) },
        );
        // Ein Vorgang ist fort, einer hat ein To-do mehr — beide Bäume neu.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return ergebnis;
    },

    /**
     * Zwei Vorgänge verschmelzen (PO 21.09.2026): `quelleVorgangId` geht in
     * `zielVorgangId` auf, der Betreff des Ziels bleibt die Überschrift.
     *
     * Alles wandert mit — To-dos, Notizen, Mails, Anrufe, Termine, Dokumente.
     * Hat das Ziel danach To-dos, zieht der Server seinen bis dahin freien
     * Schriftwechsel in ein erstes To-do: In einem Vorgang MIT To-dos gehört
     * alles einem To-do (PO). Die Quelle bleibt weich gelöscht —
     * wiederherstellbar, ihr Inhalt kommt dabei aber nicht mit zurück.
     */
    async verschmelzeVorgaenge(
        zielVorgangId: number,
        quelleVorgangId: number,
        /**
         * Was NICHT mitwandern soll — nur beim Zusammenführen von Duplikaten
         * („Anhänge und Notizen kann man einzeln abwählen", PO 21.09.2026).
         * Abgewähltes bleibt am Quellvorgang und geht mit ihm in den
         * Papierkorb; gelöscht wird nichts. Das Ziehen nutzt den Parameter
         * nicht — dort ist alles verschiedene Arbeit.
         */
        ohne?: { ohneNotizen?: number[]; ohneDokumente?: number[] },
    ): Promise<{ todos: number; eintraege: number }> {
        const ergebnis = await request<{ todos: number; eintraege: number }>(
            apiUrl(`todo/vorgang/${zielVorgangId}/verschmelzen`),
            { method: "POST", body: JSON.stringify({ quelleVorgangId, ...ohne }) },
        );
        // Ein Vorgang ist fort, einer hat alles übernommen — beide Bäume neu.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return ergebnis;
    },

    /**
     * Ein To-do aus seinem Vorgang lösen und ihm einen eigenen geben
     * (PO 22.09.2026) — die Gegenrichtung zu `verschiebeVorgangAlsTodo`.
     *
     * Der neue Vorgang trägt genau dieses eine To-do, mit allem, was daran
     * hängt. Der alte bleibt stehen, auch wenn es sein letztes war: Eine
     * vergebene Vorgangsnummer zu vernichten, wäre eine Entscheidung, die
     * niemand getroffen hat.
     */
    async todoAlsEigenenVorgang(
        todoId: number,
    ): Promise<{ vorgangId: number; umgehaengt: number }> {
        const ergebnis = await request<{ vorgangId: number; umgehaengt: number }>(
            apiUrl(`todo/${todoId}/als-vorgang`),
            { method: "POST" },
        );
        // Ein Vorgang ist neu, einer hat ein To-do verloren.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return ergebnis;
    },

    /**
     * Aus einem Vorgang zwei machen (PO 28.09.2026): Die gewählten To-dos
     * bilden samt allem, was an ihnen hängt, einen NEUEN Vorgang mit eigenem
     * Betreff. Mindestens ein To-do muss bleiben — sonst lehnt der Server ab.
     */
    async teileVorgang(
        vorgangId: number,
        todoIds: number[],
        titel: string,
    ): Promise<{ vorgangId: number; todos: number; eintraege: number }> {
        const ergebnis = await request<{ vorgangId: number; todos: number; eintraege: number }>(
            apiUrl(`todo/vorgang/${vorgangId}/teilen`),
            { method: "POST", body: JSON.stringify({ todoIds, titel }) },
        );
        // Ein Vorgang ist neu, einer hat To-dos verloren.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return ergebnis;
    },

    /**
     * Ausgewählte To-dos eines Vorgangs in einen anderen übernehmen
     * (PO 22.09.2026) — der vierte Fall der Gegenüberstellung.
     *
     * Alles, was an den gewählten To-dos hängt, wandert mit. Was nicht gewählt
     * wurde, bleibt — und mit ihm der Quellvorgang. Nur wenn nichts übrig
     * bleibt, geht er in den Papierkorb; `quelleGeloescht` sagt, was passiert
     * ist.
     */
    async uebernimmTodosAusVorgang(
        zielVorgangId: number,
        quelleVorgangId: number,
        todoIds: number[],
    ): Promise<{ todos: number; eintraege: number; quelleGeloescht: boolean; verbliebene: number }> {
        const ergebnis = await request<{
            todos: number;
            eintraege: number;
            quelleGeloescht: boolean;
            verbliebene: number;
        }>(apiUrl(`todo/vorgang/${zielVorgangId}/todos-uebernehmen`), {
            method: "POST",
            body: JSON.stringify({ quelleVorgangId, todoIds }),
        });
        // Zwei Vorgänge haben sich verändert — Prefix statt Exakt-Keys.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return ergebnis;
    },

    /**
     * F4-Härtung (Befund 2): Beteiligte (bearbeiter/hauptverantwortlich) eines
     * To-dos über den dedizierten, autorisierten Endpoint setzen — der generische
     * PUT ignoriert diese Felder serverseitig. Nur geänderte Felder mitschicken.
     */
    async updateTodoBeteiligte(
        id: number,
        data: { bearbeiter?: string[]; hauptverantwortlich?: string[] },
    ): Promise<TodoDto> {
        const updated = await request<TodoDto>(apiUrl(`todo/${id}/beteiligte`), {
            method: "PUT",
            body: JSON.stringify(data),
        });
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return updated;
    },

    /**
     * Zwei To-dos desselben Vorgangs zusammenführen (P6, PO 16.09.2026):
     * `quelleTodoId` geht in `zielId` auf. Mails, Dokumente, Anrufe, Termine,
     * Notizen und Verknüpfungen wandern serverseitig in EINER Transaktion mit;
     * die Quelle bleibt weich gelöscht (wiederherstellbar — die umgehängten
     * Einträge kommen dabei aber nicht zurück).
     */
    async mergeTodo(
        zielId: number,
        body: {
            quelleTodoId: number;
            titel: string;
            hauptverantwortlich: string[];
            bearbeiter: string[];
            zuInformierende: string[];
            kontaktId: number | null;
            risikoIds: number[];
            vertragIds: number[];
            twv: string | null;
            frist: string | null;
        },
    ): Promise<TodoDto> {
        const merged = await request<TodoDto>(apiUrl(`todo/${zielId}/merge`), {
            method: "POST",
            body: JSON.stringify(body),
        });
        // Zwei To-dos sind betroffen, dazu der Fortschritt des Vorgangs — wie
        // bei createTodo per Prefix, sonst bleiben die per-Kontakt-Listen stale.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
        return merged;
    },

    async deleteTodo(id: number): Promise<void> {
        await request<void>(apiUrl(`todo/${id}`), { method: "DELETE" });
        // Prefix statt Exakt-Keys (wie createTodo): sonst bleiben die
        // per-Kontakt-Listen `todo.byLinked.*` (Vorgänge-Tab) + die Draft-
        // Varianten `todo.all.*` bis zum TTL stale.
        invalidateByPrefix("todo.");
        invalidateByPrefix("vorgang.");
    },

    /** Upcoming Wiedervorlagen (TWV) for the current user — topbar list. */
    async getMyTwv(): Promise<TwvItemDto[]> {
        return request<TwvItemDto[]>(apiUrl("vorgang/twv/me"));
    },

    /**
     * Kaskaden-Auflösung der K/R/V-Zeile (Detail-Remake SP2): Vertrag → Kontakt
     * (+ Risiko bei eindeutiger Zuordnung), Risiko → Kontakt — inkl. Anzeige-
     * Summaries fürs direkte Vorbefüllen.
     */
    async resolveLink(params: { contractId?: number; riskObjectId?: number }): Promise<{
        contactId: number | null;
        riskObjectId: number | null;
        kontakt: { id: number; lastname: string | null; firstName: string | null } | null;
        risiko: RisikoSummary | null;
        vertrag: VertragSummary | null;
    }> {
        const query: Record<string, number> = {};
        if (params.contractId != null) query.contractId = params.contractId;
        if (params.riskObjectId != null) query.riskObjectId = params.riskObjectId;
        return request(apiUrl("vorgang/resolve-link", query));
    },

    /**
     * Kombinierter Aktivitäts-Feed eines Vorgangs (AP9 / D16): Schriftwechsel
     * (Mails) + Stammdaten-Änderungen der verknüpften Entitäten, chronologisch
     * gemergt und nach `at` DESC sortiert.
     */
    async getActivity(
        vorgangId: number,
        params: { limit?: number; offset?: number } = {},
    ): Promise<ActivityResult> {
        const query: Record<string, number> = {};
        if (params.limit !== undefined) query.limit = params.limit;
        if (params.offset !== undefined) query.offset = params.offset;
        return request<ActivityResult>(apiUrl(`vorgang/${vorgangId}/activity`, query));
    },

    /** Aktivitäts-Feed V2 des Vorgangs (menschenlesbare Titel + Dokument-Events). */
    async getActivityV2(
        vorgangId: number,
        params: { limit?: number; offset?: number } = {},
    ): Promise<ActivityResultV2> {
        const query: Record<string, number> = {};
        if (params.limit !== undefined) query.limit = params.limit;
        if (params.offset !== undefined) query.offset = params.offset;
        return request<ActivityResultV2>(apiUrl(`vorgang/${vorgangId}/activity`, query));
    },

    /** Aktivitäts-Feed eines einzelnen Todos (Detail-Remake SP6). */
    async getTodoActivity(
        todoId: number,
        params: { limit?: number; offset?: number } = {},
    ): Promise<ActivityResultV2> {
        const query: Record<string, number> = {};
        if (params.limit !== undefined) query.limit = params.limit;
        if (params.offset !== undefined) query.offset = params.offset;
        return request<ActivityResultV2>(apiUrl(`todo/${todoId}/activity`, query));
    },
};
